import { AuthProviderOptions, LoginOptions, AuthorizationCheckResult } from '../typings/auth';
import { cacheManager } from './cacheManager';
import UserAuthorizationCheck from '../api/userAuthorizationCheck';
import logger from './logger';

interface CfpAuthState {
    loading: boolean;
    isRegistered: boolean;
    isAuthenticated: boolean;
  }

interface CfpAuthStateChangeCallback {
    (state: CfpAuthState): void;
}

export class CfpAuthUtils {
    private cfpAuthState: CfpAuthState;
    private stateChangeCallbacks: CfpAuthStateChangeCallback[] = [];
    private config: any;
    private authorizationInProgress: boolean = false;
    private initializationInProgress: boolean = false;

    constructor() {
        this.cfpAuthState = {
            loading: false,
            isRegistered: false,
            isAuthenticated: false,
        };
    }

    // Public getters for state properties
    public get loading(): boolean {
        return this.cfpAuthState.loading;
    }

    public get isRegistered(): boolean {
        return this.cfpAuthState.isRegistered;
    }

    public get isAuthenticated(): boolean {
        return this.cfpAuthState.isAuthenticated;
    }

    // Method to initialize with config
    public async initialize(config: any): Promise<void> {
      // Prevent multiple simultaneous initializations
      if (this.initializationInProgress) {
        logger.info('🔄 Initialization already in progress, skipping duplicate call');
        return;
      }
      
      this.initializationInProgress = true;
      
      try {
        this.config = config;
        await this.registerCfpAuthProvider();
      } finally {
        this.initializationInProgress = false;
      }
    }

        // Method to subscribe to state changes
  public onStateChange(callback: CfpAuthStateChangeCallback): () => void {
    this.stateChangeCallbacks.push(callback);
    // Return unsubscribe function
    return () => {
      const index = this.stateChangeCallbacks.indexOf(callback);
      if (index > -1) {
        this.stateChangeCallbacks.splice(index, 1);
      }
    };
  }


  // Method to update auth state and notify subscribers
  private updateAuthState(updates: Partial<CfpAuthState>): void {
    logger.debug('🔄 Updating CFP auth state:', updates);
    this.cfpAuthState = { ...this.cfpAuthState, ...updates };
    this.stateChangeCallbacks.forEach(callback => callback(this.cfpAuthState));
  }


  private getAuthProviderOptions(): AuthProviderOptions {
    const aps_environment = 'prod';
    return {
      clientId: this.config.clientId,
      redirectUri: `${window.location.origin}/redirect`,
      redirectUriPopup: `${window.location.origin}/redirect`,
      environment: aps_environment,
      cacheLocation: 'memory',
      logoutRedirectUri: "",
      useRefreshTokens: true,
    };
  }

  // Helper method to validate redirect URI configuration
  private validateRedirectUri(): { isValid: boolean; message: string } {
    const redirectUri = `${window.location.origin}/redirect`;
    const currentProtocol = window.location.protocol;
    const currentHost = window.location.host;
    
    if (!redirectUri) {
      return { isValid: false, message: 'Redirect URI is not configured' };
    }
    
    if (!redirectUri.startsWith('http')) {
      return { isValid: false, message: 'Redirect URI must use HTTP/HTTPS protocol' };
    }
    
    // Check if redirect URI matches current origin for localhost development
    if (currentHost.includes('localhost') || currentHost.includes('127.0.0.1')) {
      const redirectHost = new URL(redirectUri).host;
      if (redirectHost !== currentHost) {
        return { 
          isValid: false, 
          message: `Redirect URI host (${redirectHost}) does not match current host (${currentHost})` 
        };
      }
    }
    
    // Check protocol consistency
    const redirectProtocol = redirectUri.startsWith('https') ? 'https:' : 'http:';
    if (redirectProtocol !== currentProtocol && !currentHost.includes('localhost')) {
      return { 
        isValid: false, 
        message: `Protocol mismatch: current (${currentProtocol}) vs redirect (${redirectProtocol})` 
      };
    }
    
    return { isValid: true, message: 'Redirect URI is valid' };
  }

  // Helper function to wait for CFP scripts to load
  private async waitForCfpScripts(maxRetries: number = 10, delay: number = 500): Promise<boolean> {
    for (let i = 0; i < maxRetries; i++) {
      if (typeof window.cfp !== 'undefined' && window.cfp.providers?.auth) {
        logger.info('✅ CFP scripts loaded successfully');
        return true;
      }
      logger.debug(`⏳ Waiting for CFP scripts to load... (attempt ${i + 1}/${maxRetries});`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    logger.error('❌ CFP scripts failed to load after maximum retries');
    return false;
  }

  public async handleRegister(): Promise<void> {
   

    try {
      const options = this.getAuthProviderOptions();

      await window.cfp.providers.auth.register(options);

      
    } catch (error) {
      logger.error('❌ Registration failed:', error);
    } finally {
      this.updateAuthState({ isRegistered: true });
    }
  };


  // Helper function to register auth provider (only once)
  public async registerCfpAuthProvider(): Promise<boolean> {
    logger.info('🔄 Registering CFP auth provider...');
    
    if (this.cfpAuthState.isRegistered) {
        return true;
      }
  
      if (!this.config.clientId || this.config.clientId === 'YOUR_APS_CLIENT_ID') {
        logger.warn('⚠️ Please set REACT_APP_APS_CLIENT_ID in your .env file with your actual APS Client ID');
        return false;
      }
      
      // Validate redirect URI configuration
      const redirectValidation = this.validateRedirectUri();
      if (!redirectValidation.isValid) {
        logger.warn(`⚠️ Redirect URI validation failed: ${redirectValidation.message}`);
        return false;
      }

      // Ensure we're running on HTTPS in production or localhost for development
      const currentProtocol = window.location.protocol;
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (currentProtocol !== 'https:' && !isLocalhost) {
        logger.warn('⚠️ CFP requires HTTPS in production environments');
        return false;
      }
  
      // Wait for CFP scripts to load
      const cfpLoaded = await this.waitForCfpScripts();
      if (!cfpLoaded) {
        logger.error('❌ CFP scripts not available');
        return false;
      }
  
      try {
        const options = this.getAuthProviderOptions();
        logger.debug("options", options);
        
        // Initialize localization with error handling
        try {
          window.cfp.providers.localization.init('en-US');
        } catch (localizationError) {
          logger.warn('⚠️ Failed to initialize CFP localization:', localizationError);
          // Continue with registration even if localization fails
        }
        
        // Register auth provider with comprehensive error handling
        await window.cfp.providers.auth.register(options);
        
        this.updateAuthState({ isRegistered: true });
        logger.info('✅ Auth provider registered successfully');
        return true;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        
        // Handle specific CFP errors gracefully
        if (errorMessage.includes('CFP_AUTH_0004')) {
          logger.info('ℹ️ Auth provider already registered (expected in React StrictMode)');
          this.updateAuthState({ isRegistered: true });
          return true;
        }
        
        // Handle SecurityError specifically
        if (errorMessage.includes('SecurityError') || errorMessage.includes('Blocked a frame')) {
          logger.warn('⚠️ SecurityError detected - this may be due to protocol mismatch or CORS issues');
          logger.warn('💡 Try running on HTTPS or ensure redirect URI matches current protocol');
          logger.warn(`Current location: ${window.location.href}`);
          logger.warn(`Redirect URI: ${window.location.origin}/redirect`);
          return false;
        }
        
        // Handle other CFP-specific errors
        if (errorMessage.includes('CFP_AUTH_')) {
          logger.error(`❌ CFP Auth Error: ${errorMessage}`);
          return false;
        }
        
        logger.error(`❌ Registration failed: ${errorMessage}`);
        return false;
      }
  }

    // Method to handle the login process
    public async login(): Promise<void> {
        logger.info('🔄 Starting CFP login process...');
        this.updateAuthState({ loading: true });
        
        try {
            // Ensure CFP is available before attempting login
            const cfpLoaded = await this.waitForCfpScripts();
            if (!cfpLoaded) {
                throw new Error('CFP scripts not available');
            }
            
            if (!this.cfpAuthState.isRegistered) {
                logger.info('🔄 Auth provider not registered, attempting to register...');
                const registered = await this.registerCfpAuthProvider();
                if (!registered) {
                    throw new Error('Failed to register auth provider');
                }
            }
            
            const loginOptions: LoginOptions = { loginMethod: 'redirect' };
            await window.cfp.providers.auth.login(loginOptions);

            this.updateAuthState({ loading: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            logger.error('❌ Login error:', error);
            logger.error(`Login failed: ${errorMessage}`);
            this.updateAuthState({ loading: false });
            throw error; // Re-throw to allow component to handle
        }
    }

    // Method to handle the logout process
    public async logout(logoutRedirectUri?: string): Promise<void> {
        logger.info('🔄 Starting CFP logout process...');
        this.updateAuthState({ loading: true });
        try {
            if (typeof window.cfp !== 'undefined' && window.cfp.providers?.auth) {
                // Use provided redirect URI or default to origin
                const redirectUri = logoutRedirectUri || window.location.origin;
                
                await window.cfp.providers.auth.logout({
                    logoutRedirectUri: redirectUri
                });
                
                // Targeted cache clearing instead of wiping all storage
                // Removes only keys managed by CacheManager (user info, id token, auth state, app config)
                cacheManager.clearCache();
                // Reset authorization flag on logout
                this.authorizationInProgress = false;
                this.updateAuthState({ isAuthenticated: false, loading: false });
            }
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            logger.error('❌ Logout error:', error);
            logger.error(`Logout failed: ${errorMessage}`);
            this.updateAuthState({ loading: false });
        }
    }

    // Method to check current authentication status
    public async checkAuthStatus(): Promise<void> {
        try {
            // Wait for CFP scripts to load if not already available
            if (typeof window.cfp === 'undefined' || !window.cfp.providers?.auth) {
                logger.info('⏳ CFP provider not ready, waiting for scripts...');
                const cfpLoaded = await this.waitForCfpScripts(5, 300); // Wait up to 1.5 seconds
                
                if (!cfpLoaded) {
                    logger.warn('⚠️ CFP scripts not available, setting isAuthenticated to false');
                    this.updateAuthState({ isAuthenticated: false });
                    return;
                }
            }
            
            const loggedIn = await window.cfp.providers.auth.isLoggedIn();
            this.updateAuthState({ isAuthenticated: loggedIn });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            logger.error(`❌ Failed to check authentication status: ${errorMessage}`);
            this.updateAuthState({ isAuthenticated: false });
        }
    }   

    public handleOAuthCallback = (): void => {
        logger.info('🔄 Handling CFP OAuth callback...');
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('code') || urlParams.has('state')) {
          logger.info('OAuth callback detected. Processing login...');
          // After callback, check if user is logged in
          this.checkLoginAfterCallback();
         
        }
      };
      
      public checkLoginAfterCallback = async (): Promise<AuthorizationCheckResult | null> => {
        logger.info('🔄 Checking login after callback...');
        
        // Prevent multiple simultaneous calls
        if (this.authorizationInProgress) {
          logger.warn('⚠️ Authorization already in progress, skipping duplicate call');
          return null;
        }
        
        this.authorizationInProgress = true;
        
        try {
          const loggedIn = await window.cfp.providers.auth.isLoggedIn();
          if (loggedIn) {
            if (typeof window.cfp !== 'undefined' && window.cfp.providers?.auth) {
                const idToken = await window.cfp.providers.auth.getIdToken();
                this.cacheIdToken(idToken);
                
                // Fetch and cache user info
                const authCheck = new UserAuthorizationCheck();
                const userInfoResult = await authCheck.CheckAuthorization(idToken);
                
                if (!UserAuthorizationCheck.IsError(userInfoResult)) {
                  logger.info('✅ User info fetched successfully:', userInfoResult);
                  this.cacheUserInfo(userInfoResult);
                  this.updateAuthState({ isAuthenticated: true, loading: false });
                  return userInfoResult;
                } else {
                  logger.error('❌ Failed to fetch user info:', userInfoResult);
                  this.updateAuthState({ isAuthenticated: false, loading: false });
                  return userInfoResult;
                }
              }
          } else {
            this.updateAuthState({ isAuthenticated: false, loading: false });
          }
        } catch (error) {
          logger.error('Failed to verify login status after callback');
          this.updateAuthState({ isAuthenticated: false, loading: false });
        } finally {
          this.authorizationInProgress = false;
        }
        return null;
      };

      private cacheIdToken(token: string): void {
        cacheManager.cacheIdToken(token);
      }

      // Method to get ID token
      public getIdToken(): string | null {
        try {
          return cacheManager.getCachedIdToken();
        } catch (error) {
          logger.error('❌ Failed to get ID token:', error);
          return null;
        }
      }

      // Method to cache user info
      public cacheUserInfo(userInfo: any): void {
        try {
          cacheManager.cacheUserInfo(userInfo);
        } catch (error) {
          logger.error('❌ Failed to cache user info:', error);
        }
      }

      // Method to get cached user info
      public getCachedUserInfo(): any {
        try {
          return cacheManager.getCachedUserInfo();
        } catch (error) {
          logger.error('❌ Failed to get cached user info:', error);
          return null;
        }
      }
            /**
   * Generic revalidation helper to be used by page-level actions prior to sensitive operations.*/
  public  revalidateAuthentication(): any {
    logger.info('🔍 Auth Revalidation: starting');
    
        const cachedUserInfo = this.getCachedUserInfo?.();

        const isValid =  !!cachedUserInfo;
    
    if (!isValid) {
      logger.debug("User is not loggedin. ")
      return false;
    }
    return true;
  }

}





